/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useMemo, useCallback } from "react";

// antd:
import { LoadingOutlined } from "@ant-design/icons";
import { Drawer, Spin } from "antd"; // Tag

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getTheme, getLng, getDict, getElementState, getUser, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

type Tdrawer = {
  closable:boolean | {"aria-label": "Close Button"},
  zIndex:number,
  height?:number | string,
  width?:number | string,
  size?:"default" | "large",
  placement?:"right" | "left" | "top" | "bottom",
  autoFocus?:boolean,
  mask?:boolean,
  // loading?:boolean
};

const RZDrawer:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZDrawer.displayName = "Drawer";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  // const values = Form.useWatch([], form);
  // const [requiredMark, setRequiredMarkType] = useState<RequiredMark>("optional");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const elState = useAppSelector(getElementState(rootAttributes.key, fullPath)); // value, visible, disabled, loading, userStyle, userClass
  const {color, backgroundColor} = elState.attributes;

  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (elState?.loading) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      if (!~userClss.indexOf("uk-flex-middle")) { userClss.push("uk-flex-middle") }
      if (!~userClss.indexOf("uk-flex-center")) { userClss.push("uk-flex-center") }
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, elState?.loading, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const drawerType = useMemo<Tdrawer>(() => {
    const typ:Tdrawer = {closable: {"aria-label": "Close Button"}, size: "large", placement: "right", zIndex: 1000, mask: true};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (tt === "large") { typ.size = "large" }
      else if (tt === "small") { typ.size = "default" }
      else if (rz.arrContains(["top", "bottom", "left", "right"], tt)) { typ.placement = tt }
      else if (rz.arrContains(["autoFocus", "autofocus"], tt)) { typ.autoFocus = true }
    });

    if (attributes?.size) { typ.size = attributes.size }
    // eslint-disable-next-line max-len
    if (attributes?.closable) { typ.closable = (rz.isString(attributes.closable) && attributes.closable.startsWith("{") && attributes.closable.endsWith("}") ? rz.objFromJSON(rz.strReplaceAll(attributes.closable, "'", "\"")).trim() : (attributes.closable !== "false")) }
    if (attributes?.width) { typ.width = (rz.canbeNumber(attributes.width) ? Number(attributes.width) : attributes.width) }
    if (attributes?.height) { typ.height = (rz.canbeNumber(attributes.height) ? Number(attributes.height) : attributes.height) }
    if (attributes?.zindex || attributes?.zIndex) { typ.zIndex = Number(attributes.zindex || attributes.zIndex) }
    if (attributes?.autoFocus || attributes?.autofocus) { typ.autoFocus = ((attributes.autoFocus || attributes.autofocus) === "true") }
    if (attributes?.placement || attributes?.place) { typ.placement = (attributes.placement || attributes.place) }
    if (elState?.attributes?.placement || elState?.attributes?.place) { typ.placement = (elState.attributes.placement || elState.attributes.place) }
    if (attributes?.mask) { typ.mask = (attributes.mask === "true") }

    return typ;
  }, [type, attributes, elState?.attributes]);

  const isMobile = useMemo(() => (rz.isMobileDevice(navigator.userAgent)), []);

  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);

  const afterOpenChange = useCallback((event) => {
    if (fnc.config?.isDev) console.log({action: "<Drawer key='" + key + "'> -> onOpen()"});

    const attrAction = (attributes.afterOpenChange || attributes.onOpen || attributes.onopen);
    if (rootNamespace && attrAction && rootNamespace[attrAction]) {
      const clientFNC = rootNamespace[attrAction];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, event, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, event, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, rootNamespace, attributes, user, lng, dct, safe]);

  const onClose = useCallback((event) => {
    if (fnc.config?.isDev) console.log({action: "<Drawer key='" + key + "'> -> onClose()"});
    // @ts-ignore
    const closeSelf = () => { dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {visible: false}})); };

    const attrAction = (attributes.onClose || attributes.onclose);
    if (!(rootNamespace && attrAction && rootNamespace[attrAction])) { closeSelf(); return; }

    const clientFNC = rootNamespace[attrAction];
    if (!rz.isFunction(clientFNC)) { closeSelf(); return; }

    const applyResult = (ret) => {
      if (rz.isUndefinedOrNull(ret)) { closeSelf(); return; }
      if (ret?.err) { console.log(ret.err) }
      if (ret?.state?.fullPath && ret?.state?.newState) {
        dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        return;
      }
      closeSelf();
    };

    const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, event, safe});
    if (rz.isPromise(ret)) { ret.then((resolved) => applyResult(resolved)).catch(() => closeSelf()); return; }

    applyResult(ret);
  }, [key, attributes, rootNamespace, rootAttributes.key, user, lng, dct, dispatch, fullPath, safe]);

  // Debug
  // useEffect(() => { console.log({attributes, elState, children}) }, [elState, attributes, children]);

  // Global Form-API to use it inside the clients-script:
  // ++++++++++++++++++++++++++++++++
  const changeState = (params) => {
    if (params && params.fullPath && params.newState) {
      dispatch(changeAppState({appkey: params.appkey || rootAttributes.key, fullPath: params.fullPath, newState: params.newState}));
    }
  };
  useEffect(() => {
    // @ts-ignore
    if (!window.changeState) { window.changeState = changeState }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootAttributes.key, fullPath]);
  // ++++++++++++++++++++++++++++++++

  return (
    <Drawer
      key={key} className={clsu.join(" ")} style={styl}
      title={title}
      open={elState.visible}
      closable={{"aria-label": "Close Button"}}
      zIndex={drawerType.zIndex}
      height={drawerType.height}
      width={isMobile ? "100%" : drawerType.width}
      size={drawerType.size}
      placement={drawerType.placement}
      autoFocus={drawerType.autoFocus}
      mask={drawerType.mask}
      afterOpenChange={afterOpenChange}
      onClose={onClose}
    >
      {
        elState.loading
          ? (<div key={key} className={clsu.join(" ")} style={styl}><div className="rz-width-expand"><Spin indicator={spinIcon} /></div></div>)
          : (children || elState.value)
      }
    </Drawer>
  );
}

export default RZDrawer;
/* eslint-enable @typescript-eslint/no-unused-vars */
